// 3rd-party modules
import moment from "moment-timezone";
import { isMobile } from "react-device-detect";
import { Tooltip } from "antd";
import { useEffect, useState } from "react";

// project modules
import { apiCall } from "../../../helpers/apiHelper";
import Button from "../../shared/button";
import CustomChart from "../../shared/chart/chart";
import DashboardWidget from "../../global/dashboardWidget";
import DataTable, { TableColumn } from "../../shared/list/dataTable";
import Loader from "../../shared/loader";
import Select from "../../shared/inputs/select";

// apis
import * as AccountKiboshDeviceApi from '../../../apis/accountKiboshDeviceApi';
import * as DashApi from '../../../apis/dashApi';

// models
import { Account } from "../../../models/account";
import { AccountKiboshDevice } from "../../../models/accountKiboshDevice";
import { DataSourceRequest, FilterDescriptor, PaginationDescriptor, SortDescriptor } from "../../../models/dataSourceRequest";
import { AccountLatencyLog } from "../../../models/accountDevice";

// defines
import { CONSTANTS } from "../../../helpers/defines";

type Props = {
  account?: Account;
  kiboshDeviceReference?: string;
  onCancel?: () => void;
  onSave?: (account: Account) => void;
};

export default function AccountLatencyLogs({ account,  kiboshDeviceReference, onCancel, onSave }: Props) {
  const chartOptions: any = {
    fill: {
      type: 'solid',
      opacity: 0.2,
      colors: ['#4c9484', '#4c9484']
    },
    colors: ['#4c9484', '#4c9484'],
    chart: {
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top', // top, center, bottom
        },
      area: {
        fillTo: 'origin',
        },
      }
    },
    stroke: { show: true, width: 2, colors: ['#4c9484', '#4c9484'], curve: "smooth" },

    dataLabels: {
      enabled: false,
      formatter: function (val: any) {
        return `${Number(val).toFixed(2)} ms`;
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"],
        fill: ['#000']
      }
    },

    xaxis: {
      position: 'bottom',
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false
      },
      tooltip: {
        enabled: false,
      },
      tickAmount: 10,

    },
    yaxis: {
      min: 0.0,
      axisBorder: {
        show: false
      },
      axisTicks: {
        show: false,
      },
      labels: {
        show: true,
        formatter: function (val: any) {
          return `${Number(val).toFixed(2)} ms`;
        }
      },
    }
  };
  const [accountKiboshDevices, setAccountKiboshDevices] = useState<AccountKiboshDevice[]>([]);
  const [currentAccountKiboshDeviceReference, setCurrentAccountKiboshDeviceReference] = useState<string>("");
  const [latencyDays, setLatencyDays] = useState<string[]>([]);
  const [rawLatencyLogs, setRawLatencyLogs] = useState<AccountLatencyLog[]>([]);
  const [latencyLogs, setLatencyLogs] = useState<AccountLatencyLog[]>([]);
  const [currentLatencyDay, setCurrentLatencyDay] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [loadingAccountKiboshDevices, setLoadingAccountKiboshDevices] = useState(false);
  const [loadingLatencyDays, setLoadingLatencyDays] = useState(false);
  const [regetFullData, setRegetFullData] = useState<boolean>(false);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [medianLatency, setMedianLatency] = useState<number|undefined>(0);
  const [maxLatency, setMaxLatency] = useState<any>(0);
  const [openLatencyChartInModal, setOpenLatencyChartInModal] = useState(false);
  let abortController = new AbortController();



  useEffect(() => {
    if (regetFullData) {
      if (!kiboshDeviceReference) {
        getAccountKiboshDevicesAsync();
      } else {
        getLatencyDaysAsync();
      }
      setRegetFullData(false);
    }
  }, [regetFullData]);

  useEffect(() => {
    setRegetFullData(true);

  }, [])




  const getAccountKiboshDevicesAsync = async () => {
    setAccountKiboshDevices(await getAccountKiboshDevices({}, abortController.signal));
  }

  const getAccountKiboshDevices = async (request?: DataSourceRequest, abortSignal?: AbortSignal) => {
    setLoadingAccountKiboshDevices(true);
    const response = await apiCall(AccountKiboshDeviceApi.getAccountKiboshDevices(account?.accountId!, request, abortSignal));
    setLoadingAccountKiboshDevices(false);

    return response.success ? AccountKiboshDevice.toArrayOfClass(response.data?.value || []) : [];
  };

  const getLatencyDaysAsync = async (loadDay?: string) => {
    setLatencyDays(await getLatencyDays(abortController.signal, loadDay));

  }

  const getLatencyDays = async (abortSignal?: AbortSignal, loadDay?: string) => {
    setLoadingLatencyDays(true);
    setLoading(true);
    const response = await apiCall(DashApi.getLatencyDays(kiboshDeviceReference || currentAccountKiboshDeviceReference, abortSignal));



    let ld = response.success ? response.data?.value || [] : [];
    let cd = loadDay ? loadDay : currentLatencyDay;
    cd = !cd && ld.length > 0 ? ld[0] : cd
    if( !currentLatencyDay && !loadDay) {
      setCurrentLatencyDay(cd)
    }

    if(!!cd) {
      const response2 = await apiCall(DashApi.getLatency(kiboshDeviceReference || currentAccountKiboshDeviceReference, cd, abortSignal));

      let rlogs = response2.success ? AccountLatencyLog.toArrayOfClass(response2.data?.value || []) : []
      //let smoothedLogs = smoothLogs(rlogs, 5)
      let smoothedLogs = rlogs;

      setTotalRecords(smoothedLogs.length);
      setRawLatencyLogs(rlogs);
      setLatencyLogs(smoothedLogs);

      if(rlogs.length > 0) {
        let ping = rlogs.map( (log) => log.pingMs ).filter( (ping) => ping != null && ping > 0.0 ).sort((a, b) => (!!a) && (!!b) ? a - b : 0)
        let max:any = ping[ping.length-1]
        max = !!max ? parseFloat(max).toFixed(2) : max
        setMedianLatency(getMedian(ping).toFixed(2))
	setMaxLatency(max)
      }
      


    }
   
    setLoadingLatencyDays(false);
    setLoading(false);

    return ld
  };

  const getMedian = (sorted:any) => {  
    const middleIndex = Math.floor(sorted.length/2);  
  
    if (sorted.length % 2 === 0) {  
        return (sorted[middleIndex-1] + sorted[middleIndex]) / 2;  
    } else {  
        return sorted[middleIndex];  
    }
}

  const smoothLogs= (logs: any, windowSize: number) => {
    let smoothedLogs:any = []
    let li=0;
    while(li < logs.length-1 ) {
      let windowCount = 0.0;
      let windowSum = 0.0;
      let windowTimestamp:any = logs[li].pingTimestamp;
      while(windowCount < windowSize && li < logs.length-1) {
        windowTimestamp = logs[li].pingTimestamp;
        windowSum = windowSum + ( logs[li]?.pingMs ? logs[li].pingMs : 0.0 )
        windowCount = windowCount + 1.0
        li = li + 1
      }
      if(windowCount > 0) { 
        smoothedLogs.push( { "pingTimestamp" : windowTimestamp, "pingMs" : (windowSum/windowCount) } )
      }

    }
    //last time point isn't smoothed so it matches latest reported
    if(li < logs.length) {
      smoothedLogs.push( logs[li] )
    }
    return smoothedLogs;
  }





  const onAccountKiboshDeviceChange = (kiboshDeviceReference: any) => {
    setCurrentAccountKiboshDeviceReference(kiboshDeviceReference);
  }


  const onLogDaysChange = async (day:any) => {
    setCurrentLatencyDay(day);
    getLatencyDaysAsync(day)
  }




  return (
    <>
      {loading && <Loader /> }
      <view data-scroll="" >
        <group data-space="15" data-gap="15" data-border="" data-align="center" > 
            <>
              {!kiboshDeviceReference && (
                <>
                  <Select
                    autoComplete=""
                    label="Kibosh Device"
                    labelPosition="left"
                    dataLength="forcefit"
                    dataMinLength="200"
                    onChange={onAccountKiboshDeviceChange}
                    loading={loadingAccountKiboshDevices}
                    allowSearch={true}
                    options={
                      accountKiboshDevices?.map((item) => {
                        return {
                          text: `${item.kiboshDeviceName!} (${item.kiboshDeviceReference!})`,
                          value: item.kiboshDeviceReference!,
                        };
                      }) || []
                    }
                    button={
                      <Button
                        material
                        icon="refresh"
                        onClick={getAccountKiboshDevicesAsync}
                      />
                    }
                  />
                </>
              )}
          <Select
            value={currentLatencyDay} //?
            autoComplete=""
            label="Display Logs For"
            labelPosition="left"
            dataLength="forcefit"
            dataMinLength="425"
            onChange={onLogDaysChange}
            loading={loadingLatencyDays}
            allowSearch={true}
            options={latencyDays || []}
            button={
              <Button
                material
                icon="refresh"
                onClick={getLatencyDaysAsync}
              />
            }
          />
          <Button
            data-position="right"
            material
            icon="refresh"
            text="Refresh"
            outline
            onClick={getLatencyDaysAsync}
          />
          </>
        
        </group>









          <group
            data-type="grid"
            data-gap="10"
            data-space="15"
            data-grid-template="300"
            data-background="highlight"
            data-border=""
          >
            <DashboardWidget
              title="Latency"
              height=""
              childSpace="0"
              headerButtonsContainer={
                <>
                  <Tooltip title="Maximize">
                    <div
                      className="button micro"
                      data-position="right"
                      onClick={() => setOpenLatencyChartInModal(true)}
                    >
                      <icon>fullscreen</icon>
                    </div>
                  </Tooltip>
                </>
              }
            >
              <CustomChart
                title="Latency"
                options={{
                  ...chartOptions,
                  xaxis: {
                    ...chartOptions.xaxis,
                    categories: latencyLogs
                      .map((logs: any) => {
                        return {
                          ...logs,
                          pingTimestamp: new Date(logs.pingTimestamp),
                        };
                      })
                      .sort(
                        (a: any, b: any) =>
                          a.pingTimestamp.getTime() - b.pingTimestamp.getTime()
                      )
                      .map((logs: any) =>
                        moment(logs.pingTimestamp).format(
                          CONSTANTS.DEFAULT_TIME_FORMAT
                        )
                      ),
                  },
                }}
                series={[
                  {
                    name: "Latency",
                    data: latencyLogs
                      .map((logs: any) => {
                        return {
                          ...logs,
                          pingTimestamp: new Date(logs.pingTimestamp),
                        };
                      })
                      .sort(
                        (a: any, b: any) =>
                          a.pingTimestamp.getTime() - b.pingTimestamp.getTime()
                      )
                      .map(
                        (logs: any) => Number(logs.pingMs)
                      ),
                  },
                ]}
                type="area"
                width="100%"
                openInModal={openLatencyChartInModal}
                onClose={() => setOpenLatencyChartInModal(false)}
              />
            </DashboardWidget>
          </group>
          <group>
            <separator data-horizontal=""></separator>
            <group data-type="column" data-column-size="160" data-wrap="no">

              <group data-space="20" data-align="center">
                <group data-direction="column" data-length='160'>
                  <text data-text-size="x-large" data-text-align="center" data-light="">{medianLatency} ms</text>
                  <group>
                    <group data-direction="column" data-fit="1" data-justify="center">
                      <text data-weight="600" data-color="main-dark">Median Latency</text>
                    </group>
                  </group>
                </group>
              </group>

              <group data-space="20" data-align="center">
                <group data-direction="column" data-length='160'>
                  <text data-text-size="x-large" data-text-align="center" data-light="">{maxLatency} ms</text>
                  <group>
                    <group data-direction="column" data-fit="1" data-justify="center">
                      <text data-weight="600" data-color="main-dark">Max. Latency</text>
                    </group>
                  </group>
                </group>
              </group>

            </group>
          </group>


      </view>
    </>
  );
}
